import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Check, CheckCircle2, X, RefreshCw, Pencil, Copy, Trash2,
  ChevronDown, AlertTriangle, Calendar, Target, Sparkles, Loader2,
  LayoutGrid, List, Filter, MoreHorizontal, Send, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { ANGLE_LABELS, LAYOUT_STYLES } from '../../data/pinTemplates';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const STATUS_CONFIG = {
  needs_review: { label: 'Trenger review', color: 'bg-amber-50 text-amber-600', dot: 'bg-amber-500' },
  approved: { label: 'Godkjent', color: 'bg-green-50 text-green-600', dot: 'bg-green-500' },
  scheduled: { label: 'Planlagt', color: 'bg-blue-50 text-blue-600', dot: 'bg-blue-500' },
  published: { label: 'Published', color: 'bg-emerald-50 text-emerald-600', dot: 'bg-emerald-500' },
  failed: { label: 'Failed', color: 'bg-red-50 text-red-600', dot: 'bg-red-500' },
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-500', dot: 'bg-stone-400' },
};

const CampaignReview = ({ campaignId, initialData, onBack, onEditPin, onApproved }) => {
  const [campaign, setCampaign] = useState(initialData || null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(!initialData);
  const [selectedPins, setSelectedPins] = useState(new Set());
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [editingPin, setEditingPin] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/campaigns/${campaignId}`, getAuth());
      setCampaign(res.data);
      setPins(res.data.pins || []);
    } catch (e) {
      console.error('Fetch campaign error:', e);
    }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => {
    if (initialData?.pins) {
      setPins(initialData.pins);
      setCampaign(initialData);
    } else {
      fetchCampaign();
    }
  }, [campaignId, initialData, fetchCampaign]);

  const toggleSelect = (pinId) => {
    setSelectedPins(prev => {
      const next = new Set(prev);
      if (next.has(pinId)) next.delete(pinId); else next.add(pinId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedPins.size === filteredPins.length) {
      setSelectedPins(new Set());
    } else {
      setSelectedPins(new Set(filteredPins.map(p => p.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedPins.size === 0) return;
    setBulkMenuOpen(false);
    try {
      await axios.post(`${API}/api/campaigns/${campaignId}/pins/bulk`, {
        pin_ids: Array.from(selectedPins),
        action,
      }, getAuth());
      setSelectedPins(new Set());
      await fetchCampaign();
    } catch (e) {
      console.error('Bulk action error:', e);
    }
  };

  const handleDeletePin = async (pinId) => {
    try {
      await axios.post(`${API}/api/campaigns/${campaignId}/pins/bulk`, {
        pin_ids: [pinId],
        action: 'delete',
      }, getAuth());
      await fetchCampaign();
    } catch (e) {
      console.error('Delete pin error:', e);
    }
  };

  const handleDuplicatePin = async (pin) => {
    try {
      const res = await axios.post(`${API}/api/pin-studio/pins`, {
        ...pin,
        campaign_id: campaignId,
        status: 'needs_review',
        id: undefined,
        _id: undefined,
      }, getAuth());
      // Add to campaign
      const newPinId = res.data.id;
      const currentIds = campaign?.pin_ids || [];
      await axios.put(`${API}/api/campaigns/${campaignId}`, {}, getAuth());
      await fetchCampaign();
    } catch (e) {
      console.error('Duplicate error:', e);
    }
  };

  const handleUpdatePin = async (pinId, updates) => {
    try {
      await axios.put(`${API}/api/pin-studio/pins/${pinId}`, updates, getAuth());
      setEditingPin(null);
      setEditForm({});
      await fetchCampaign();
    } catch (e) {
      console.error('Update pin error:', e);
    }
  };

  const handleRegenerateCopy = async (pin) => {
    setRegenerating(pin.id);
    try {
      const res = await axios.post(`${API}/api/pin-studio/generate`, {
        source_product_title: pin.source_product_title || 'Product',
        source_product_description: '',
        source_product_category: '',
        count: 1,
        angles: [pin.marketing_angle || 'educational'],
        destination_url: pin.destination_url || '',
      }, getAuth());

      const newPin = res.data.pins?.[0];
      if (newPin) {
        await axios.put(`${API}/api/pin-studio/pins/${pin.id}`, {
          headline: newPin.headline,
          subheadline: newPin.subheadline,
          cta: newPin.cta,
          pinterest_title: newPin.pinterest_title,
          pinterest_description: newPin.pinterest_description,
          pinterest_alt_text: newPin.pinterest_alt_text,
          pinterest_keywords: newPin.pinterest_keywords,
        }, getAuth());
        await fetchCampaign();
      }
    } catch (e) {
      console.error('Regenerate error:', e);
    }
    setRegenerating(null);
  };

  const handleApproveAndSchedule = async () => {
    setApproving(true);
    try {
      const res = await axios.post(`${API}/api/campaigns/${campaignId}/approve`, {}, getAuth());
      onApproved?.(res.data);
      await fetchCampaign();
    } catch (e) {
      console.error('Approve error:', e);
    }
    setApproving(false);
  };

  const filteredPins = filterStatus === 'all'
    ? pins
    : pins.filter(p => p.status === filterStatus || (filterStatus === 'needs_review' && p.status === 'draft'));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#B49A88]" />
      </div>
    );
  }

  const repCheck = campaign?.repetition_check;
  const stats = campaign?.stats || {};
  const reviewable = (stats.needs_review || 0) + (stats.approved || 0);

  return (
    <div className="space-y-5" data-testid="campaign-review">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="flex items-center gap-1.5 text-stone-400 hover:text-stone-600 font-sans text-xs font-medium mb-2 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back til kampanjer
          </button>
          <h2 className="font-serif font-bold text-stone-800 text-xl">{campaign?.name || 'Kampanje'}</h2>
          <div className="flex items-center gap-3 mt-1">
            <span className="font-sans text-xs text-stone-400">{pins.length} pins</span>
            {campaign?.goal && (
              <span className="font-sans text-[10px] font-medium text-[#B49A88] bg-[#B49A88]/10 px-2 py-0.5 rounded-full">
                {campaign.goal.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>
        {reviewable > 0 && (
          <Button
            data-testid="approve-schedule-btn"
            onClick={handleApproveAndSchedule}
            disabled={approving}
            className="bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl px-5 py-3 text-xs font-sans font-semibold disabled:opacity-50"
          >
            {approving ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Planning...</>
            ) : (
              <><Send className="w-3.5 h-3.5 mr-1.5" /> Godkjenn & planlegg</>
            )}
          </Button>
        )}
      </div>

      {/* Repetition warnings */}
      {repCheck && !repCheck.ok && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="repetition-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-sans font-semibold text-amber-800 text-xs mb-1">Kampanjen kan bli repetitiv</p>
              {repCheck.warnings.map((w, i) => (
                <p key={i} className="font-sans text-[11px] text-amber-700 leading-relaxed">• {w}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div className="flex items-center gap-3 flex-wrap">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = key === 'needs_review'
            ? pins.filter(p => p.status === 'needs_review' || p.status === 'draft').length
            : pins.filter(p => p.status === key).length;
          if (count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => setFilterStatus(filterStatus === key ? 'all' : key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-sans font-semibold transition-all ${
                filterStatus === key ? cfg.color + ' ring-1 ring-offset-1' : 'bg-stone-50 text-stone-500 hover:bg-stone-100'
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
              {cfg.label}: {count}
            </button>
          );
        })}
        {filterStatus !== 'all' && (
          <button onClick={() => setFilterStatus('all')} className="text-[10px] font-sans text-stone-400 hover:text-stone-600 underline">
            Show alle
          </button>
        )}
      </div>

      {/* Bulk actions */}
      {selectedPins.size > 0 && (
        <div className="bg-stone-900 rounded-xl p-3 flex items-center justify-between" data-testid="bulk-actions-bar">
          <div className="flex items-center gap-3">
            <span className="font-sans text-xs text-white font-semibold">{selectedPins.size} selected</span>
            <button onClick={selectAll} className="text-[10px] font-sans text-stone-400 hover:text-white underline">
              {selectedPins.size === filteredPins.length ? 'Remove all' : 'Choose all'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => handleBulkAction('approve')}
              className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-3 py-1.5 text-[10px] font-sans font-semibold h-auto"
              data-testid="bulk-approve-btn"
            >
              <Check className="w-3 h-3 mr-1" /> Godkjenn
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleBulkAction('delete')}
              className="rounded-lg px-3 py-1.5 text-[10px] font-sans font-semibold h-auto"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
            <button onClick={() => setSelectedPins(new Set())} className="p-1.5 text-stone-400 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* View toggle + select all */}
      <div className="flex items-center justify-between">
        <button
          onClick={selectAll}
          className="text-[10px] font-sans font-medium text-stone-400 hover:text-stone-600 transition-colors"
          data-testid="select-all-btn"
        >
          {selectedPins.size === filteredPins.length && filteredPins.length > 0 ? 'Remove all selected' : 'Choose all'}
        </button>
        <div className="flex items-center gap-1 bg-stone-50 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-stone-700' : 'text-stone-400'}`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-stone-700' : 'text-stone-400'}`}
          >
            <List className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Pin grid */}
      <div className={viewMode === 'grid'
        ? 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'
        : 'space-y-3'
      }>
        {filteredPins.map((pin) => {
          const angleInfo = ANGLE_LABELS[pin.marketing_angle] || { label: pin.marketing_angle || '—', color: '#888' };
          const statusInfo = STATUS_CONFIG[pin.status] || STATUS_CONFIG.draft;
          const selected = selectedPins.has(pin.id);
          const isEditing = editingPin === pin.id;
          const templateInfo = LAYOUT_STYLES[pin.template_id] || { label: pin.template_id };

          if (viewMode === 'list') {
            return (
              <div
                key={pin.id}
                data-testid={`review-pin-${pin.id}`}
                className={`bg-white rounded-xl border p-4 flex items-center gap-4 transition-all ${
                  selected ? 'border-[#E60023]/30 bg-[#E60023]/5' : 'border-stone-100 hover:border-stone-200'
                }`}
              >
                <button onClick={() => toggleSelect(pin.id)} className="flex-shrink-0">
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                    selected ? 'border-[#E60023] bg-[#E60023]' : 'border-stone-200'
                  }`}>
                    {selected && <Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
                <div className="w-10 h-14 rounded-lg bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-serif font-bold text-stone-400 text-[8px] text-center px-1 leading-tight">{pin.headline?.slice(0, 20)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans font-medium text-stone-700 text-xs truncate">{pin.pinterest_title || pin.headline}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[9px] font-sans font-bold px-1.5 py-0.5 rounded-full" style={{ background: angleInfo.color + '15', color: angleInfo.color }}>{angleInfo.label}</span>
                    <span className="text-[9px] font-sans text-stone-400">{templateInfo.label}</span>
                    <span className={`text-[9px] font-sans font-semibold px-1.5 py-0.5 rounded-full ${statusInfo.color}`}>{statusInfo.label}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => onEditPin(pin.id)} className="p-1.5 rounded-lg hover:bg-stone-50 text-stone-400 hover:text-stone-600 transition-colors" title="Edit">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => handleRegenerateCopy(pin)} disabled={regenerating === pin.id} className="p-1.5 rounded-lg hover:bg-stone-50 text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-30" title="Regenerer copy">
                    {regenerating === pin.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  </button>
                  <button onClick={() => handleDeletePin(pin.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-300 hover:text-red-500 transition-colors" title="Delete">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          }

          // Grid view
          return (
            <div
              key={pin.id}
              data-testid={`review-pin-${pin.id}`}
              className={`bg-white rounded-2xl border overflow-hidden transition-all group ${
                selected ? 'border-[#E60023]/30 shadow-md' : 'border-stone-100 hover:shadow-md'
              }`}
            >
              {/* Select checkbox */}
              <div className="p-2 flex items-center justify-between">
                <button onClick={() => toggleSelect(pin.id)}>
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                    selected ? 'border-[#E60023] bg-[#E60023]' : 'border-stone-200 hover:border-stone-300'
                  }`}>
                    {selected && <Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
                <span className={`text-[9px] font-sans font-semibold px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              </div>

              {/* Pin preview */}
              <div className="aspect-[2/3] bg-gradient-to-b from-stone-50 to-stone-100 p-5 flex flex-col justify-center items-center text-center relative mx-2 rounded-xl">
                <span
                  className="absolute top-2.5 left-2.5 text-[8px] font-sans font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: angleInfo.color + '15', color: angleInfo.color }}
                >
                  {angleInfo.label}
                </span>
                <span className="absolute top-2.5 right-2.5 text-[8px] font-sans text-stone-300">
                  {templateInfo.label}
                </span>
                <p className="font-serif font-bold text-stone-800 text-sm leading-tight mb-1.5">{pin.headline || 'Untitled'}</p>
                {pin.subheadline && <p className="font-sans text-stone-500 text-[9px] leading-relaxed mb-2">{pin.subheadline}</p>}
                {pin.cta && <span className="px-3 py-1 bg-[#B49A88] text-white text-[8px] font-sans font-semibold rounded-full">{pin.cta}</span>}
              </div>

              {/* Pin info + actions */}
              <div className="p-3">
                <p className="font-sans font-medium text-stone-700 text-[11px] truncate mb-1">{pin.pinterest_title || pin.headline}</p>
                {pin.source_product_title && (
                  <p className="font-sans text-[9px] text-stone-300 truncate mb-2">Fra: {pin.source_product_title}</p>
                )}

                {/* Quick actions */}
                <div className="flex items-center gap-1 pt-2 border-t border-stone-50">
                  <button
                    onClick={() => onEditPin(pin.id)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[9px] font-sans font-semibold text-stone-500 hover:bg-stone-50 transition-colors"
                    data-testid={`edit-pin-${pin.id}`}
                  >
                    <Pencil className="w-2.5 h-2.5" /> Edit
                  </button>
                  <button
                    onClick={() => handleRegenerateCopy(pin)}
                    disabled={regenerating === pin.id}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[9px] font-sans font-semibold text-stone-500 hover:bg-stone-50 transition-colors disabled:opacity-30"
                    data-testid={`regen-pin-${pin.id}`}
                  >
                    {regenerating === pin.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />}
                    Copy
                  </button>
                  <button
                    onClick={() => handleDeletePin(pin.id)}
                    className="p-1.5 rounded-lg text-stone-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                    data-testid={`del-pin-${pin.id}`}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredPins.length === 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
          <p className="font-sans text-sm text-stone-500">None pins i dette filteret</p>
        </div>
      )}
    </div>
  );
};

export default CampaignReview;
